// app/views/components/shared/PaymentSummary.tsx
import { useState } from 'react'
import { Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatCents } from '@/lib/utils'
import { ChevronDown, ChevronRight, CheckCircle2, FileText, RotateCcw } from 'lucide-react'

type Payment = {
  id: string
  service_fee_cents: number
  gov_fee_cents: number
  total_cents: number
  currency?: string
  paid: boolean
  paid_at: string | null
  invoice_number?: string | null
  receipt_number?: string | null
  refunded?: boolean
  refund_amount_cents?: number
}

type AdminActions = {
  confirmPaymentPath?: string
  refundPath?: string
  invoicePath?: string
}

type PaymentSummaryProps = {
  payment: Payment | null
  adminActions?: AdminActions
  collapsible?: boolean
}

export function PaymentSummary({ payment, adminActions, collapsible = true }: PaymentSummaryProps) {
  const [open, setOpen] = useState(!collapsible)

  if (!payment) {
    return (
      <section className="border rounded-lg">
        <div className="px-4 py-3">
          <h2 className="text-sm font-medium text-muted-foreground">No payment created yet</h2>
        </div>
      </section>
    )
  }

  const header = (
    <div
      className={`px-4 py-3 flex items-center justify-between ${collapsible ? 'cursor-pointer' : ''} ${open ? 'border-b' : ''}`}
      onClick={collapsible ? () => setOpen(!open) : undefined}
    >
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-medium">Payment</h2>
        {payment.paid ? (
          <Badge className="font-normal text-xs bg-green-600 text-white"><CheckCircle2 className="w-3 h-3 mr-1" />Paid</Badge>
        ) : (
          <Badge variant="outline" className="font-normal text-xs">Unpaid</Badge>
        )}
        {payment.refunded && (
          <Badge className="font-normal text-xs bg-red-600 text-white">Refunded</Badge>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{formatCents(payment.total_cents)}</span>
        {collapsible && (open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />)}
      </div>
    </div>
  )

  return (
    <section className="border rounded-lg">
      {header}
      {open && (
        <div className="px-4 py-3 space-y-3">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground text-xs">Service Fee</p>
              <p className="font-medium">{formatCents(payment.service_fee_cents)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Gov Fee</p>
              <p className="font-medium">{formatCents(payment.gov_fee_cents)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Total</p>
              <p className="font-medium">{formatCents(payment.total_cents)}</p>
            </div>
          </div>

          {(payment.invoice_number || payment.receipt_number) && (
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              {payment.invoice_number && (
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />Invoice #{payment.invoice_number}
                </span>
              )}
              {payment.receipt_number && (
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />Receipt #{payment.receipt_number}
                </span>
              )}
            </div>
          )}

          {payment.refunded && payment.refund_amount_cents && (
            <div className="flex items-center gap-1 text-xs text-red-600">
              <RotateCcw className="w-3 h-3" />
              Refunded {formatCents(payment.refund_amount_cents)}
            </div>
          )}

          {adminActions && (
            <div className="flex items-center gap-2 pt-2 border-t">
              {adminActions.invoicePath && (
                <Button variant="outline" size="sm" className="h-7 text-xs" asChild>
                  <Link href={adminActions.invoicePath}>View Invoice</Link>
                </Button>
              )}
              {adminActions.confirmPaymentPath && !payment.paid && (
                <Button variant="outline" size="sm" className="h-7 text-xs" asChild>
                  <Link href={adminActions.confirmPaymentPath} method="post" as="button">Confirm Payment</Link>
                </Button>
              )}
              {adminActions.refundPath && payment.paid && !payment.refunded && (
                <Button variant="outline" size="sm" className="h-7 text-xs" asChild>
                  <Link href={adminActions.refundPath}>Issue Refund</Link>
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
